import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

type MenuValue = string | number;

interface DropdownOption {
  value: MenuValue;
  label: string;
  icon?: React.ReactNode;
}

interface ResponsiveDropMenuProps {
  value: MenuValue;
  onChange: (value: MenuValue) => void;
  options: DropdownOption[];
  className?: string;
  buttonClassName?: string;
}

const ResponsiveDropMenu: React.FC<ResponsiveDropMenuProps> = ({
  value,
  onChange,
  options,
  className = "",
  buttonClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;

    const update = () => {
      if (!buttonRef.current) return;
      const r = buttonRef.current.getBoundingClientRect();
      setPos({ top: r.bottom, left: r.left, width: r.width });
    };

    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t) || menuRef.current?.contains(t))
        return;
      setOpen(false);
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    document.addEventListener("mousedown", onDocDown);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      document.removeEventListener("mousedown", onDocDown);
    };
  }, [open]);

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: pos.width,
            zIndex: 9999,
          }}
          className="mt-1"
        >
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors duration-150 flex items-center gap-3 border-b border-neutral-800 last:border-b-0 ${
                  value === opt.value
                    ? "bg-primary text-black"
                    : "text-neutral-300 hover:bg-neutral-800 hover:text-white"
                }`}
              >
                {opt.icon && <span>{opt.icon}</span>}
                <span className="flex-1">{opt.label}</span>
                {value === opt.value && <span className="text-black">✓</span>}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-w-40 rounded-lg px-4 py-2.5 text-sm font-medium focus:outline-none transition-all duration-200 shadow-sm hover:shadow-md text-left flex items-center justify-between ${
          buttonClassName
            ? buttonClassName
            : "bg-white border-2 border-gray-200 hover:border-primary-300 focus:border-primary text-gray-700 focus:ring-2 focus:ring-primary-200"
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {selected?.icon && <span>{selected.icon}</span>}
          {selected?.label || "Select option"}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""} ${buttonClassName?.includes("text-black") ? "text-black" : "text-gray-500"}`}
        />
      </button>
      {menu}
    </div>
  );
};

export default ResponsiveDropMenu;
